import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AccountApi, VideoApi } from "../Api";
import { User } from "../User";
export const useCurrentUserProfile = ()=>{
    const userApi = new AccountApi();
    const queryClient = useQueryClient();
    const useCurrentUserProfileQuery = ()=>useQuery([
            "userProfile",
            "me"
        ], async ()=>await userApi.getCurrentAccount().then(User.fromExtendedAccountDto));
    const useUserProfileMutationQuery = function() {
        let { onSuccess , onError  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return useMutation((param)=>{
            let { userId , data  } = param;
            return userApi.updateAccount(userId, data);
        }, {
            onSuccess: function(data) {
                for(var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
                    args[_key - 1] = arguments[_key];
                }
                void queryClient.invalidateQueries();
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, ...args);
            },
            onError
        });
    };
    const useChangeUserPasswordMutationQuery = function() {
        let { onSuccess , onError  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return useMutation((param)=>{
            let { userId , ...rest } = param;
            return userApi.changePassword(userId, rest);
        }, {
            onSuccess: function(data) {
                for(var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
                    args[_key - 1] = arguments[_key];
                }
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, ...args);
            },
            onError
        });
    };
    const useDeleteAccountMutationQuery = function() {
        let { onSuccess , onError  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return useMutation((userId)=>userApi.deleteAccount(userId), {
            onSuccess: function(data) {
                for(var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++){
                    args[_key - 1] = arguments[_key];
                }
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, ...args);
            },
            onError
        });
    };
    const useCurrentUserStickersQuery = (pagination)=>{
        return useQuery([
            "userStickers",
            "me",
            pagination
        ], async ()=>await userApi.getMyStickers(pagination), {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    const useSubscribeAccountMutationQuery = function() {
        let { onSuccess , onError  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return useMutation((userId)=>userApi.subscribe(userId), {
            onSuccess,
            onError
        });
    };
    const useCurrentUserSubscribedQuery = (pagination)=>{
        return useInfiniteQuery([
            "subscriptions",
            "subscribed",
            "me",
            "infinite"
        ], async (param)=>{
            let { pageParam =pagination  } = param;
            return await userApi.getMySubscribed(pageParam);
        }, {
            getNextPageParam: (param)=>{
                let { meta  } = param;
                return meta.currentPage === meta.totalPages || meta.totalPages === 0 ? undefined : {
                    page: meta.currentPage + 1,
                    limit: meta.itemsPerPage,
                    serach: meta.search
                };
            },
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    const useCurrentUserSubscribersQuery = (pagination)=>{
        return useInfiniteQuery([
            "subscriptions",
            "subscribers",
            "me",
            "infinite"
        ], async (param)=>{
            let { pageParam =pagination  } = param;
            return await userApi.getMySubscribers(pageParam);
        }, {
            getNextPageParam: (param)=>{
                let { meta  } = param;
                return meta.currentPage === meta.totalPages || meta.totalPages === 0 ? undefined : {
                    page: meta.currentPage + 1,
                    limit: meta.itemsPerPage,
                    serach: meta.search
                };
            },
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    const useCurrentUserStickersInfiniteQuery = (pagination)=>{
        return useInfiniteQuery([
            "userStickers",
            "me",
            "infinite"
        ], async (param)=>{
            let { pageParam =pagination  } = param;
            return userApi.getMyStickers(pageParam);
        }, {
            getNextPageParam: (param)=>{
                let { meta  } = param;
                return meta.currentPage === meta.totalPages ? undefined : {
                    page: meta.currentPage + 1,
                    limit: meta.itemsPerPage,
                    serach: meta.search
                };
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    const useCurrentUserResourceCountQuery = ()=>{
        return useQuery([
            "userCount",
            "me"
        ], async ()=>await userApi.getMyResourceCount(), {
            refetchOnMount: false,
            refetchOnWindowFocus: false
        });
    };
    return {
        useSubscribeAccountMutationQuery,
        useCurrentUserSubscribedQuery,
        useCurrentUserSubscribersQuery,
        useUserProfileMutationQuery,
        useCurrentUserProfileQuery,
        useChangeUserPasswordMutationQuery,
        useDeleteAccountMutationQuery,
        useCurrentUserStickersQuery,
        useCurrentUserResourceCountQuery,
        useCurrentUserStickersInfiniteQuery
    };
};
export const useCurrentUserVideos = ()=>{
    const userApi = new AccountApi();
    const videoApi = new VideoApi();
    const queryClient = useQueryClient();
    const useCurrentUserVideosQuery = (pagination)=>useQuery([
            "userVideos",
            "me",
            pagination
        ], async ()=>await userApi.getMyVideos(pagination), {
            keepPreviousData: true
        });
    const useDeleteUserVideoMutationQuery = function() {
        let { onSuccess , onError  } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return useMutation((param)=>{
            let { videoId  } = param;
            return videoApi.delete(videoId);
        }, {
            onSuccess: function() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                void queryClient.invalidateQueries([
                    "userVideos",
                    "me"
                ]);
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(...args);
            },
            onError
        });
    };
    return {
        useCurrentUserVideosQuery,
        useDeleteUserVideoMutationQuery
    };
};
export const useUserProfile = ()=>{
    const userApi = new AccountApi();
    const useUserProfileQuery = (id)=>useQuery([
            "userProfile",
            id
        ], async ()=>await userApi.getAccount(id).then(User.fromExtendedAccountDto), {
            refetchOnMount: false
        });
    const useUserResourceCountQuery = (id)=>{
        return useQuery([
            "userCount",
            id
        ], async ()=>await userApi.getUserResourceCount(id), {
            refetchOnMount: false,
            refetchOnWindowFocus: false
        });
    };
    return {
        useUserProfileQuery,
        useUserResourceCountQuery
    };
};
export const useUserVideos = ()=>{
    const userApi = new AccountApi();
    const useUserVideosQuery = (id, pagination)=>{
        return useQuery([
            "userVideos",
            id,
            pagination
        ], async ()=>await userApi.getUserVideos(id, pagination), {
            refetchOnMount: false,
            keepPreviousData: true
        });
    };
    const useUserStickersQuery = (id, pagination)=>{
        return useQuery([
            "userStickers",
            id,
            pagination
        ], async ()=>await userApi.getUserStickers(id, pagination), {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    const useUserStickersInfiniteQuery = (id, pagination)=>{
        return useInfiniteQuery([
            "userStickers",
            id,
            "infinite"
        ], async (param)=>{
            let { pageParam =pagination  } = param;
            return userApi.getUserStickers(id, pageParam);
        }, {
            getNextPageParam: (param)=>{
                let { meta  } = param;
                return meta.currentPage === meta.totalPages ? undefined : {
                    page: meta.currentPage + 1,
                    limit: meta.itemsPerPage,
                    serach: meta.search
                };
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true
        });
    };
    return {
        useUserStickersInfiniteQuery,
        useUserVideosQuery,
        useUserStickersQuery
    };
};
